import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Info } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

/**
 * Admin → Integrations → Zoopla Fields
 * Mirrors RightmoveMappingSection but for the Zoopla RTL v2.3 catalogue.
 * Each row = one Zoopla JSON field with a CMS-field source or a static value.
 */
export default function ZooplaMappingSection() {
  const [rows, setRows] = useState([]);
  const [cmsFields, setCmsFields] = useState([]);
  const [loading, setLoading] = useState(true);
  // Auto-save state — "idle" | "dirty" | "saving" | "saved" | "error"
  const [autosaveState, setAutosaveState] = useState("idle");
  const autosaveTimer = useRef(null);
  const isInitialLoad = useRef(true);

  const load = async () => {
    try {
      setLoading(true);
      isInitialLoad.current = true;
      const { data } = await axios.get(`${API}/admin/integrations/zoopla-mapping`, { withCredentials: true });
      setRows(data.mappings || []);
      setCmsFields(data.cms_fields || []);
      setAutosaveState("idle");
      setTimeout(() => { isInitialLoad.current = false; }, 60);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Zoopla mapping");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  // Debounced auto-save whenever `rows` changes (except on initial hydration).
  useEffect(() => {
    if (isInitialLoad.current) return;
    if (!rows.length) return;
    setAutosaveState("dirty");
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        setAutosaveState("saving");
        const payload = {
          mappings: rows.map((r) => ({
            zoopla_field: r.zoopla_field,
            cms_field: r.cms_field || "",
            static_value: r.static_value || "",
          })),
        };
        await axios.put(`${API}/admin/integrations/zoopla-mapping`, payload, { withCredentials: true });
        setAutosaveState("saved");
      } catch (e) {
        setAutosaveState("error");
        toast.error(e.response?.data?.detail || "Auto-save failed");
      }
    }, 800);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [rows]);

  // Group rows by their "group" key.
  const grouped = rows.reduce((acc, r) => {
    (acc[r.group] = acc[r.group] || []).push(r);
    return acc;
  }, {});

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Zoopla Field Mapping (RTL v2.3)</CardTitle>
          <CardDescription>
            Map each Zoopla JSON field to a CMS property field, or fix a static value.
            Documentation: <a href="https://www.zoopla.co.uk/realtime-documentation/" target="_blank" rel="noopener noreferrer" className="underline">Real-Time Listings</a> · Host: <code className="text-xs">realtime-listings-api.webservices.zpg.co.uk</code>
          </CardDescription>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs flex items-center gap-1.5" data-testid="zoopla-autosave-indicator">
            {autosaveState === "saving" && (<><Loader2 className="h-3 w-3 animate-spin" /> Auto-saving…</>)}
            {autosaveState === "dirty" && (<><span className="inline-block h-2 w-2 rounded-full bg-amber-500" /> Unsaved</>)}
            {autosaveState === "saved" && (<><span className="inline-block h-2 w-2 rounded-full bg-emerald-500" /> Saved</>)}
            {autosaveState === "error" && (<><span className="inline-block h-2 w-2 rounded-full bg-red-500" /> Auto-save failed</>)}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="zoopla-mapping-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          Object.entries(grouped).map(([group, entries]) => (
            <div key={group} className="space-y-2">
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{group}</div>
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left p-2">Zoopla Field</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2 w-1/3">CMS Field</th>
                      <th className="text-left p-2 w-1/4">Static Value (overrides CMS)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((r) => {
                      const idx = rows.findIndex((x) => x.zoopla_field === r.zoopla_field);
                      const staticSet = Boolean((r.static_value || "").trim());
                      return (
                        <tr key={r.zoopla_field} className="border-t">
                          <td className="p-2 align-top">
                            <div className="flex items-center gap-2">
                              <code className="text-xs">{r.zoopla_field}</code>
                              {r.required && <Badge variant="destructive" className="text-[10px]">required</Badge>}
                            </div>
                            {r.description && (
                              <div className="text-[11px] text-muted-foreground mt-1 flex items-start gap-1">
                                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                                <span>{r.description}</span>
                              </div>
                            )}
                            {Array.isArray(r.allowed_values) && r.allowed_values.length > 0 && (
                              <div className="text-[10px] text-muted-foreground mt-1">
                                Allowed: {r.allowed_values.slice(0, 8).join(", ")}
                                {r.allowed_values.length > 8 ? ` (+${r.allowed_values.length - 8})` : ""}
                              </div>
                            )}
                          </td>
                          <td className="p-2 align-top text-xs text-muted-foreground">{r.type}</td>
                          <td className="p-2 align-top">
                            <select
                              value={r.cms_field || ""}
                              onChange={(e) => updateRow(idx, { cms_field: e.target.value })}
                              disabled={staticSet}
                              className={`w-full h-8 border rounded px-2 text-xs bg-background ${staticSet ? "opacity-40 cursor-not-allowed" : ""}`}
                              data-testid={`zoopla-cms-field-${r.zoopla_field}`}
                            >
                              <option value="">— select CMS field —</option>
                              {cmsFields.map((f) => (
                                <option key={f.key} value={f.key}>
                                  {f.label || f.key}
                                </option>
                              ))}
                            </select>
                            {staticSet && (
                              <div className="text-[10px] text-amber-600 mt-1">Overridden by static value →</div>
                            )}
                          </td>
                          <td className="p-2 align-top">
                            <Input
                              value={r.static_value || ""}
                              onChange={(e) => updateRow(idx, { static_value: e.target.value })}
                              placeholder="—"
                              className="h-8 text-xs"
                              data-testid={`zoopla-static-${r.zoopla_field}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
